"use client";

import { useState, useEffect } from "react";
import { Timer, MapPin, Trophy, Clock, AlertCircle, Calendar } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";

interface MatchContextSidebarProps {
  homeTeam: string;
  awayTeam: string;
  league: string;
  matchDate?: string | null;
}

interface MatchData {
  id: string;
  homeTeam: string;
  homeAbbr: string;
  homeScore: number;
  awayTeam: string;
  awayAbbr: string;
  awayScore: number;
  status: "scheduled" | "in_progress" | "final";
  statusDetail: string;
  startTime: string;
  venue?: string;
  clock?: string;
  period?: string;
  found: boolean;
}

// League display names
const LEAGUE_NAMES: Record<string, string> = {
  "uefa.champions": "Champions League",
  "uefa.europa": "Europa League",
  "eng.1": "Premier League",
  "esp.1": "La Liga",
  "ita.1": "Serie A",
  "ger.1": "Bundesliga",
  "fra.1": "Ligue 1",
  "nba": "NBA",
  "nfl": "NFL",
  "mlb": "MLB",
  "nhl": "NHL",
  "soccer": "Soccer",
};

export default function MatchContextSidebar({
  homeTeam,
  awayTeam,
  league,
  matchDate,
}: MatchContextSidebarProps) {
  const [matchData, setMatchData] = useState<MatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState<string>("");
  const visRef = useVisibilityRef();

  const leagueName = LEAGUE_NAMES[league] || league;

  useEffect(() => {
    const fetchMatch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (matchDate) params.set("date", matchDate);

        const res = await fetch(
          `/api/match/${encodeURIComponent(league)}/${encodeURIComponent(homeTeam)}/${encodeURIComponent(awayTeam)}?${params}`
        );

        if (res.ok) {
          const data = await res.json();
          setMatchData(data);
        }
      } catch (error) {
      }
      setLoading(false);
    };

    fetchMatch();

    // Refresh every 30 seconds if match is in progress or scheduled
    const interval = setInterval(() => { if (visRef.current) fetchMatch(); }, 30000);
    return () => clearInterval(interval);
  }, [homeTeam, awayTeam, league, matchDate]);

  // Countdown timer
  useEffect(() => {
    if (!matchData?.startTime || matchData.status !== "scheduled") {
      setCountdown("");
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const matchTime = new Date(matchData.startTime).getTime();
      const diff = matchTime - now;

      if (diff <= 0) {
        setCountdown("Starting soon...");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setCountdown(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [matchData?.startTime, matchData?.status]);

  if (loading) {
    return (
      <div className="bg-[#18181D] rounded-[5px] border border-gray-800 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-gray-400 animate-pulse" />
          <span className="text-white font-satoshi font-bold">Loading match...</span>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-[#1A1A1A] rounded-[5px]" />
          <div className="h-10 bg-[#1A1A1A] rounded-[5px]" />
        </div>
      </div>
    );
  }

  if (!matchData || !matchData.found) {
    return (
      <div className="bg-[#18181D] rounded-[5px] border border-gray-800 p-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-5 h-5 text-gray-500" />
          <span className="text-gray-400 font-satoshi font-bold text-sm">{leagueName}</span>
        </div>
        <div className="text-center py-4">
          <p className="text-white font-satoshi font-bold mb-1">{homeTeam}</p>
          <p className="text-gray-500 text-sm mb-1">vs</p>
          <p className="text-white font-satoshi font-bold mb-3">{awayTeam}</p>
          <p className="text-gray-500 text-xs">Match data unavailable</p>
        </div>
      </div>
    );
  }

  const isLive = matchData.status === "in_progress";
  const isFinal = matchData.status === "final";
  const isScheduled = matchData.status === "scheduled";

  return (
    <div className="space-y-3">
      {/* Main Match Card */}
      <div
        className={`rounded-[5px] overflow-hidden ${
          isLive
            ? "bg-[#18181D] border border-[#FF1744]"
            : isFinal
            ? "bg-[#18181D] border border-gray-700"
            : "bg-[#18181D] border border-gray-800"
        }`}
      >
        {/* Header */}
        <div
          className={`px-4 py-2 flex items-center justify-between ${
            isLive
              ? "bg-[#FF1744]"
              : isFinal
              ? "bg-[#1A1A1A] border-b border-gray-800"
              : "bg-[#1A1A1A] border-b border-gray-800"
          }`}
        >
          <div className="flex items-center gap-2">
            {isLive && <div className="w-2 h-2 bg-white rounded-[5px] animate-pulse" />}
            <span
              className={`text-xs font-inter-tight font-bold uppercase tracking-wider ${
                isLive ? "text-white" : "text-gray-400"
              }`}
            >
              {isLive ? "LIVE" : isFinal ? "FINAL" : leagueName}
            </span>
          </div>
          {isLive && matchData.statusDetail && (
            <span className="text-white/80 text-xs font-inter-tight">
              {matchData.statusDetail}
            </span>
          )}
        </div>

        {/* Score Section */}
        <div className="p-4">
          {/* Home Team */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[5px] bg-gray-700/50 flex items-center justify-center">
                <span className="text-white font-bold text-xs">
                  {matchData.homeAbbr || matchData.homeTeam.slice(0, 3).toUpperCase()}
                </span>
              </div>
              <span className="text-white font-satoshi font-bold text-sm">
                {matchData.homeTeam}
              </span>
            </div>
            <span
              className={`text-2xl font-monument font-bold ${
                isLive || isFinal ? "text-white" : "text-gray-600"
              }`}
            >
              {isScheduled ? "-" : matchData.homeScore}
            </span>
          </div>

          {/* Away Team */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[5px] bg-gray-500/20 flex items-center justify-center">
                <span className="text-gray-400 font-bold text-xs">
                  {matchData.awayAbbr || matchData.awayTeam.slice(0, 3).toUpperCase()}
                </span>
              </div>
              <span className="text-white font-satoshi font-bold text-sm">
                {matchData.awayTeam}
              </span>
            </div>
            <span
              className={`text-2xl font-monument font-bold ${
                isLive || isFinal ? "text-white" : "text-gray-600"
              }`}
            >
              {isScheduled ? "-" : matchData.awayScore}
            </span>
          </div>
        </div>

        {/* Status / Countdown */}
        {isScheduled && (
          <div className="px-4 pb-4">
            <div className="bg-[#1A1A1A] rounded-[5px] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-xs font-inter-tight flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Kickoff
                </span>
                <span className="text-white text-xs font-inter-tight">
                  {new Date(matchData.startTime).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              {countdown && (
                <div className="flex items-center justify-center gap-2 bg-[#1A1A1A] rounded-[5px] py-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-white font-monument font-bold text-lg">
                    {countdown}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live Clock */}
        {isLive && matchData.clock && (
          <div className="px-4 pb-4">
            <div className="bg-[#FF1744]/20 rounded-[5px] p-3 flex items-center justify-center gap-2">
              <Timer className="w-4 h-4 text-[#FF1744]" />
              <span className="text-white font-monument font-bold">
                {matchData.clock}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Venue Info */}
      {matchData.venue && (
        <div className="bg-[#18181D] border border-gray-800 rounded-[5px] p-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span className="text-gray-300 text-xs font-satoshi">{matchData.venue}</span>
          </div>
        </div>
      )}

      {/* League Badge */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-gray-400" />
            <span className="text-white font-satoshi font-bold text-sm">{leagueName}</span>
          </div>
          <span className="text-gray-500 text-xs font-inter-tight">Live from ESPN</span>
        </div>
      </div>
    </div>
  );
}
